import React, { useState } from 'react';
import './App.css';
import axios from 'axios';
import SubstreamsPanel from './substreams/SubstreamsPanel';

// Protocol-owned contracts, each verified to return subgraphs *and* substreams
// packages. Deliberately no bare token contracts: a token like USDC is indexed
// by hundreds of unrelated protocols, so the protocol term derived from subgraph
// names lands on whoever happens to top the list rather than on the token.
const EXAMPLES = [
  { label: 'Uniswap V3', note: 'factory', address: '0x1F98431c8aD98523631AE4a59f267346ea31F984' },
  { label: 'Uniswap V2', note: 'factory', address: '0x5C69bEe701ef814a2B6a3EDD4B1652CB9cc5aA6f' },
  { label: 'Aave V3', note: 'pool', address: '0x87870Bca3F3fD6335C3F4ce8392D69350B4fA4E2' },
  { label: 'ENS', note: 'registry', address: '0x00000000000C2E074eC69A0dFb2997BA6C7d2e1e' },
  { label: 'Curve', note: '3pool', address: '0xbEbc44782C7dB0a1A60Cb6fe97d0b483032FF1C7' },
];

function App() {
  const [contractAddress, setContractAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subgraphs, setSubgraphs] = useState([]);
  // The address a search actually ran with, as opposed to whatever is currently
  // typed — the substreams panel highlights matches against this.
  const [searchedAddress, setSearchedAddress] = useState('');

  // Placeholder for your API key - in production, use environment variables
  const API_KEY = "f8e7d16f7523dcd8181ace22f8113407";

  // Format version labels to ensure they have the "v" prefix
  const formatVersionLabel = (version) => {
    if (!version) return 'v0.0.1';
    
    // If it's already prefixed with v, return as is
    if (version.toString().toLowerCase().startsWith('v')) {
      return version;
    }
    
    // Otherwise add the v prefix
    return `v${version}`;
  };

  // Helper function to format token amounts
  const formatTokens = (amount) => {
    if (!amount) return '0';
    // Convert from wei to GRT (10^18)
    const grt = Number(amount) / 1e18;
    return grt.toLocaleString(undefined, { 
      minimumFractionDigits: 0,
      maximumFractionDigits: 2 
    });
  };

  const handleSearch = async (e, presetAddress) => {
    if (e) e.preventDefault();

    // Example chips search immediately, so the address can arrive as an argument
    // rather than via the input's state, which hasn't re-rendered yet.
    const address = (presetAddress || contractAddress).trim();
    if (!address) return;
    if (presetAddress) setContractAddress(presetAddress);

    setLoading(true);
    setError(null);
    setSubgraphs([]);
    setSearchedAddress(address);

    try {
      // Step 1: Query the Indexed Contracts subgraph
      const indexedContractsEndpoint = `https://gateway.thegraph.com/api/${API_KEY}/subgraphs/id/FMTUN6d7sY2bLnAmNEPJTqiU3iuQht6ZXurpBh71wbWR`;
      
      const contractsQuery = {
        query: `
          query GetDeployment {
            contracts(where: { contract: "${address.toLowerCase()}" }) {
              id
              contract
              deployment {
                id
              }
            }
          }
        `
      };
      
      const contractsResponse = await axios.post(indexedContractsEndpoint, contractsQuery);
      const contracts = contractsResponse.data.data.contracts;
      
      if (!contracts || contracts.length === 0) {
        setError("No subgraphs found indexing this contract");
        setLoading(false);
        return;
      }
      
      // Extract unique deployment IDs
      const deploymentIds = [...new Set(
        contracts.map(contract => contract.deployment.id)
      )];
      
      // Step 2: Query the Graph Network Subgraph for metadata
      const networkSubgraphEndpoint = `https://gateway.thegraph.com/api/${API_KEY}/subgraphs/id/DZz4kDTdmzWLWsV373w2bSmoar3umKKH9y82SUKr5qmp`;
      
      const metadataQuery = {
        query: `
          query GetSubgraphDetails($ids: [String!]) {
            subgraphDeployments(where: { ipfsHash_in: $ids }) {
              id
              ipfsHash
              versions(orderBy: version, orderDirection: desc) {
                version
                metadata {
                  label
                }
                subgraph {
                  id
                  metadata {
                    displayName
                    image
                  }
                  currentSignalledTokens
                }
              }
            }
          }
        `,
        variables: {
          ids: deploymentIds
        }
      };
      
      const metadataResponse = await axios.post(networkSubgraphEndpoint, metadataQuery);
      const subgraphDeployments = metadataResponse.data.data.subgraphDeployments || [];
      
      // Format the results
      const formattedSubgraphs = subgraphDeployments.flatMap(deployment => 
        deployment.versions.map(version => ({
          id: version.subgraph.id,
          deploymentId: deployment.ipfsHash,
          name: version.subgraph.metadata?.displayName || "Unnamed Subgraph",
          version: version.version,
          label: version.metadata?.label || 'Unlabeled',
          image: version.subgraph.metadata?.image || null,
          signalledTokens: version.subgraph.currentSignalledTokens || '0'
        }))
      );
      
      // Remove duplicates by ID
      const uniqueSubgraphs = Array.from(
        new Map(formattedSubgraphs.map(sg => [sg.id, sg])).values()
      );
      
      // Sort subgraphs by name for consistent display
      uniqueSubgraphs.sort((a, b) => a.name.localeCompare(b.name));
      
      setSubgraphs(uniqueSubgraphs);
      
    } catch (err) {
      console.error("Error fetching subgraphs:", err);
      setError("Error fetching subgraphs. Please check the console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="logo-container">
          <a 
            href="https://graphtronauts.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="logo-link"
          >
            <img 
              src="https://graphtronauts.app/assets/logo-white-B7t0qwEV.svg" 
              alt="Graphtronauts Logo" 
              className="graphtronauts-logo" 
            />
            <span className="graphtronauts-text">Graphtronauts</span>
          </a>
          <a 
            href="https://linktr.ee/graphtronauts" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="linktree-link"
          >
            Linktree
          </a>
        </div>
      
        <h1>
          Search <span className="title-accent">any contract</span>
        </h1>
        <p className="tagline">
          Every subgraph indexing it, and every Substreams package for the same protocol —
          decoded to show what each one actually reads.
        </p>

        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            value={contractAddress}
            onChange={(e) => setContractAddress(e.target.value)}
            placeholder="Enter contract address (0x...)"
            className="address-input"
          />
          <button type="submit" disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </form>

        <div className="examples">
          <span className="examples-label">Try</span>
          {EXAMPLES.map((ex) => (
            <button
              key={ex.address}
              type="button"
              className={`example-chip${searchedAddress.toLowerCase() === ex.address.toLowerCase() ? ' active' : ''}`}
              disabled={loading}
              onClick={() => handleSearch(null, ex.address)}
            >
              {ex.label} <span className="example-note">{ex.note}</span>
            </button>
          ))}
        </div>

        {error && <div className="error-message">{error}</div>}

        {loading && <div className="spinner-container"><div className="spinner"></div></div>}

        {subgraphs.length > 0 && (
          <div className="results-container">
            <h2>
              {subgraphs.length} subgraph{subgraphs.length === 1 ? '' : 's'} index this contract
            </h2>
            <code className="searched-address">{searchedAddress}</code>

            <div className="subgraphs-grid">
              {subgraphs.map((subgraph) => (
                <div key={`${subgraph.id}-${subgraph.deploymentId}`} className="subgraph-card">
                  <div className="card-header">
                    {subgraph.image && (
                      <img 
                        src={subgraph.image.startsWith('ipfs://') 
                          ? `https://ipfs.io/ipfs/${subgraph.image.replace('ipfs://', '')}`
                          : subgraph.image}
                        alt={subgraph.name}
                        className="subgraph-image"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    {!subgraph.image && <div className="image-placeholder"></div>}
                  </div>
                  
                  <div className="card-content">
                    <h3 title={subgraph.name}>
                      {subgraph.name}
                      {' '}
                      <span className="version-tag">({formatVersionLabel(subgraph.label || subgraph.version)})</span>
                    </h3>
                    <p className="signal-info">Signal: {formatTokens(subgraph.signalledTokens)} GRT</p>
                    <a 
                      href={`https://thegraph.com/explorer/subgraphs/${subgraph.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="view-link"
                    >
                      See Subgraph
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {subgraphs.length > 0 && (
          <SubstreamsPanel subgraphs={subgraphs} searchedAddress={searchedAddress} />
        )}
      </header>
    </div>
  );
}

export default App;